import { useEffect, useState } from 'react';
import { useAssistantSettings, useUpdateAssistantSettings } from '../../hooks/useAssistantMail';
import { ApiError } from '../../lib/api';

function errorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    const body = err.body as { error?: string } | null;
    return body?.error ?? `Request failed (${err.status})`;
  }
  return err instanceof Error ? err.message : String(err);
}

/** Rules + AI knobs for the attention feed. Global: there is one owner. */
export function FeedSettingsCard({ onResult }: { onResult: (m: { kind: 'ok' | 'err'; text: string }) => void }) {
  const settings = useAssistantSettings();
  const update = useUpdateAssistantSettings();
  const [vip, setVip] = useState('');
  const [awaiting, setAwaiting] = useState(3);
  const [lookback, setLookback] = useState(14);
  const [aiEnabled, setAiEnabled] = useState(true);
  const [aiMax, setAiMax] = useState(100);
  const [owners, setOwners] = useState('');
  const [muted, setMuted] = useState('');

  useEffect(() => {
    const s = settings.data;
    if (!s) return;
    setVip(s.vipSenders.join('\n'));
    setAwaiting(s.awaitingReplyDays);
    setLookback(s.lookbackDays);
    setAiEnabled(s.aiEnabled);
    setAiMax(s.aiMaxMessagesPerTick);
    setOwners(s.ownerEmails.join('\n'));
    setMuted(s.mutedSenders.join('\n'));
  }, [settings.data]);

  if (settings.isLoading) return null;

  return (
    <section>
      <h2 style={{ marginBottom: 4 }}>Feed settings</h2>
      <p className="muted" style={{ marginTop: 0 }}>
        Who always gets through, how long an unanswered mail waits before it counts, and whether the AI writes summaries.
      </p>
      <form
        className="card pa-form"
        style={{ marginTop: 0 }}
        onSubmit={(e) => {
          e.preventDefault();
          const split = (s: string) => s.split(/[\n,;]+/).map((x) => x.trim()).filter(Boolean);
          update.mutate(
            { vipSenders: split(vip), mutedSenders: split(muted), awaitingReplyDays: awaiting, lookbackDays: lookback, aiEnabled, aiMaxMessagesPerTick: aiMax, ownerEmails: split(owners) },
            {
              onSuccess: () => onResult({ kind: 'ok', text: 'Feed settings saved. They apply on the next sync (or Sync now).' }),
              onError: (err) => onResult({ kind: 'err', text: errorMessage(err) }),
            },
          );
        }}
      >
        <div className="pa-form__row">
          <label>
            VIP senders (one per line; an address or @domain)
            <textarea rows={4} value={vip} onChange={(e) => setVip(e.target.value)} placeholder={'cpa@firm.com\n@court.gov'} />
          </label>
          <label>
            Your other addresses (one per line)
            <textarea rows={4} value={owners} onChange={(e) => setOwners(e.target.value)} placeholder="Connected accounts are included automatically" />
          </label>
        </div>
        <label>
          Muted senders (fed by the Junk button; one per line, an address or @domain)
          <textarea rows={3} value={muted} onChange={(e) => setMuted(e.target.value)} placeholder={'newsletter@shop.com\n@promo.example'} />
        </label>
        <div className="pa-form__row">
          <label>
            Days before "awaiting reply"
            <input type="number" min={1} max={30} value={awaiting} onChange={(e) => setAwaiting(Number(e.target.value))} />
          </label>
          <label>
            Days of mail to consider
            <input type="number" min={1} max={90} value={lookback} onChange={(e) => setLookback(Number(e.target.value))} />
          </label>
        </div>
        <div className="pa-form__row">
          <label className="pa-form__inline">
            <input type="checkbox" checked={aiEnabled} onChange={(e) => setAiEnabled(e.target.checked)} />
            AI summaries and extraction
          </label>
          <label>
            Max messages per sync sent to the AI
            <input type="number" min={0} max={1000} value={aiMax} onChange={(e) => setAiMax(Number(e.target.value))} disabled={!aiEnabled} />
          </label>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button type="submit" className="btn-primary" disabled={update.isPending}>
            {update.isPending ? 'Saving…' : 'Save'}
          </button>
        </div>
      </form>
    </section>
  );
}
